import { Inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { FacebookApiService, FacebookFormInterface, ListFormsForPageRequestInterface } from '@vendasta/facebook';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import {
  LeadAdsFormConnectionSettings,
  LeadAdsFormConnectionSettingsInterface,
  LeadsApiService,
  LeadSource,
  UpdateLeadAdsFormConnectionSettingsRequestInterface,
} from '@vendasta/prospect';
import { BehaviorSubject, combineLatest, forkJoin, Observable, of } from 'rxjs';
import { catchError, finalize, map, retry, switchMap, take } from 'rxjs/operators';

export interface FacebookFormConfiguration {
  formName: string;
  connected: boolean;
  market: string;
  formID: string;
  createdAt: Date;
  locale: string;
}

@Injectable()
export class FacebookLeadAdsFormAutomationConfigurationService {
  private readonly tableShowAlert$$ = new BehaviorSubject<boolean>(false);
  readonly tableShowAlert$ = this.tableShowAlert$$.asObservable();

  private readonly facebookFormsTableData$$ = new BehaviorSubject<FacebookFormConfiguration[]>([]);
  readonly facebookFormsTableData$ = this.facebookFormsTableData$$.asObservable();
  readonly noFormsSelected$ = this.facebookFormsTableData$.pipe(
    map((formConfigs) => formConfigs.every((form) => !form.connected)),
  );

  private readonly subscribedToForms$$ = new BehaviorSubject<boolean>(false);
  readonly subscribedToForms$ = this.subscribedToForms$$.asObservable();

  private readonly isSavingFormsConfig$$ = new BehaviorSubject<boolean>(false);
  readonly isSavingFormsConfig$ = this.isSavingFormsConfig$$.asObservable();

  constructor(
    @Inject('PARTNER_ID') private readonly partnerId$: Observable<string>,
    private readonly facebookApiService: FacebookApiService,
    private readonly leadsApiService: LeadsApiService,
    private readonly snackbarService: SnackbarService,
    private readonly translate: TranslateService,
  ) {}

  loadFacebookForms(facebookPageID: string): void {
    combineLatest([this.getForms$(facebookPageID), this.getFormSettings$(facebookPageID)])
      .pipe(map(([forms, formSettings]) => this.combineFormsAndSettings(forms, formSettings)))
      .subscribe((facebookForms) => {
        this.facebookFormsTableData$$.next(facebookForms);
      });
  }

  private getForms$(facebookPageID: string): Observable<FacebookFormInterface[]> {
    return this.partnerId$.pipe(
      map((partnerID) => {
        return <ListFormsForPageRequestInterface>{
          pageId: facebookPageID,
          partnerId: partnerID,
        };
      }),
      switchMap((request) => this.facebookApiService.listFormsForPage(request)),
      retry(1),
      map((formsResponse) => formsResponse.facebookForms),
      catchError((_) => {
        this.tableShowAlert$$.next(true);
        return of(<FacebookFormInterface[]>[]);
      }),
      take(1),
    );
  }

  private getFormSettings$(pageId: string): Observable<LeadAdsFormConnectionSettings[]> {
    return this.partnerId$.pipe(
      switchMap((pid) =>
        this.leadsApiService.listLeadAdsFormsConnectionSettings({
          partnerId: pid,
          source: LeadSource.LEAD_SOURCE_FACEBOOK,
          pageId: pageId,
        }),
      ),
      retry(1),
      map((formsResponse) => formsResponse.formAutomations),
      catchError((_) => {
        this.tableShowAlert$$.next(true);
        return of([]);
      }),
      take(1),
    );
  }

  updateFormConnectedStatus(formID: string, connected: boolean, saveRightNow: boolean): void {
    this.subscribedToForms$$.next(false);

    const currentFormsConfiguration = this.facebookFormsTableData$$.value;
    currentFormsConfiguration.forEach((form) => {
      if (form.formID === formID) {
        form.connected = connected;
        if (saveRightNow) {
          this.saveSettingsForForm(form);
        }
      }
    });
    this.facebookFormsTableData$$.next(currentFormsConfiguration);
  }

  updateMarketOnForm(formID: string, marketID: string, saveRightNow: boolean): void {
    this.subscribedToForms$$.next(false);

    const currentFormsConfiguration = this.facebookFormsTableData$$.value;
    currentFormsConfiguration.forEach((form) => {
      if (form.formID === formID) {
        form.market = marketID;
        if (saveRightNow) {
          this.saveSettingsForForm(form);
        }
      }
    });
    this.facebookFormsTableData$$.next(currentFormsConfiguration);
  }

  private combineFormsAndSettings(
    forms: FacebookFormInterface[],
    settings: LeadAdsFormConnectionSettings[],
  ): FacebookFormConfiguration[] {
    const settingsMap: { [formId: string]: LeadAdsFormConnectionSettings } = {};
    settings?.forEach((s) => {
      settingsMap[s.formId] = s;
    });

    const formsConfiguration: FacebookFormConfiguration[] = [];
    forms?.forEach((facebookForm) => {
      const defaultSettings = <LeadAdsFormConnectionSettings>{
        enabled: false,
        destinationMarketId: 'default',
      };
      const settingsForForm =
        facebookForm.leadgenFormId in settingsMap ? settingsMap[facebookForm.leadgenFormId] : defaultSettings;
      formsConfiguration.push({
        formName: facebookForm.leadgenFormName,
        formID: facebookForm.leadgenFormId,
        market: settingsForForm.destinationMarketId,
        connected: settingsForForm.enabled,
        createdAt: facebookForm.created,
        locale: facebookForm.locale,
      });
    });
    return formsConfiguration;
  }

  updateLeadAdsFormAutomations(): void {
    this.subscribedToForms$$.next(false);
    this.isSavingFormsConfig$$.next(true);

    const responses$ = this.facebookFormsTableData$.pipe(
      map((allConfigs) =>
        allConfigs.map((config) =>
          this.partnerId$.pipe(
            switchMap((partnerID) =>
              this.leadsApiService.updateLeadAdsFormConnectionSettings(<
                UpdateLeadAdsFormConnectionSettingsRequestInterface
              >{
                formAutomation: <LeadAdsFormConnectionSettingsInterface>{
                  partnerId: partnerID,
                  source: LeadSource.LEAD_SOURCE_FACEBOOK,
                  formId: config.formID,
                  enabled: config.connected,
                  destinationMarketId: config.market,
                },
              }),
            ),
            take(1), // ensure the partnerId$ observable completes, otherwise the forkJoin never completes
          ),
        ),
      ),
      switchMap((val) => forkJoin(val)),
      take(1),
      finalize(() => this.isSavingFormsConfig$$.next(false)),
    );

    const result$ = responses$.pipe(
      map((x) => x.every((response) => response.status === 200)),
      catchError((_) => {
        this.snackbarService.errorSnack(this.translate.instant('LEAD_INTEGRATIONS.FACEBOOK.FORMS.SAVE_ERROR'));
        return of(false);
      }),
      take(1),
    );

    result$.subscribe((allRequestsSuccessful) => {
      this.subscribedToForms$$.next(allRequestsSuccessful);
    });
  }

  saveSettingsForForm(settings: FacebookFormConfiguration): void {
    this.partnerId$
      .pipe(
        switchMap((partnerID) =>
          this.leadsApiService.updateLeadAdsFormConnectionSettings(<
            UpdateLeadAdsFormConnectionSettingsRequestInterface
          >{
            formAutomation: <LeadAdsFormConnectionSettingsInterface>{
              partnerId: partnerID,
              source: LeadSource.LEAD_SOURCE_FACEBOOK,
              formId: settings.formID,
              enabled: settings.connected,
              destinationMarketId: settings.market,
            },
          }),
        ),
        retry(1),
        catchError((_) => {
          this.snackbarService.errorSnack(this.translate.instant('LEAD_INTEGRATIONS.FACEBOOK.FORMS.SAVE_ERROR'));
          return of(null);
        }),
        take(1),
      )
      .subscribe();
  }
}
